import React from 'react'
import Head from 'next/head'
import queryString from 'query-string'
import { GetServerSideProps } from 'next'

import { useAppSelector } from 'redux/hooks'
import { ReduxServerProps } from 'redux/store'
import { generateInitialState } from 'redux/collectionV2/initialState'
import { CollectionGameType, CollectionTag, CollectionV2Card, COLLECTION_GAME_TYPE } from 'redux/collectionV2/types'

import { CollectionService } from 'services/collection.service'

import PageWrapper from 'components/misc/PageWrapper'
import Icon from 'components/elements/Icon'
import CardBanner from 'components/misc/CardBanner'
import HeaderControls from 'components/collectionV2/controls/Header'
import ContentControls from 'components/collectionV2/controls/Content'
import ImageView from 'components/collectionV2/views/Image'
import TextView from 'components/collectionV2/views/Text'
import LabelsView from 'components/collectionV2/views/Labels'
import Link from 'components/elements/Link'
import AvatarCircle from 'components/user/AvatarCircle'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CollectionFilterModal from 'components/collectionV2/forms/FilterModal'
import CardDetailsModal from 'components/collectionV2/cardDetails/Modal'
import KeybindsOverlay from 'components/collectionV2/misc/KeybindsOverlay'
import ImportDropWrapper from 'components/misc/ImportDropWrapper'
import PaginationControls from 'components/collectionV2/misc/PaginationControls'

import { setCollectionQueryParams } from 'utils/queryParams/collection.queryParams'
import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'
import { responseToCard } from 'utils/ResponseToResults'

import { encodeUsernameRoute } from 'utils/encoding'

import styles from './username.module.scss'

type Props = {
  owner: {
    id: number
    username: string
    avatar: string
    frame: string
  }
  page: number
  totalPages: number
  count: number
  game: CollectionGameType
}

const CollectionPageV2 = ({ owner, page, count, game, totalPages }: Props) => {
  const view = useAppSelector(state => state.collectionV2.view)
  const viewingLabels = useAppSelector(state => state.collectionV2.viewingLabels)
  const autoColumns = useAppSelector(state => state.collectionV2.autoColumns)
  const multiSelectedIds = useAppSelector(state => state.collectionV2.multiSelectedIds)

  return (
    <ImportDropWrapper isCollection>
      <PageWrapper title={`${owner.username}'s Collection - Archidekt`}>
        <Head>
          <meta property="og:type" content="website" />
          <meta property="og:image" content={owner.avatar} />
          <meta property="og:image:width" content="100" />
          <meta property="og:image:height" content="100" />
          <meta property="og:image:type" content="image/jpeg" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:label1" content="Cards" />
          <meta name="twitter:data1" content={`${count}`} />
        </Head>

        <CardBanner
          className={styles.bannerContainer}
          backgroundClassName={styles.banner}
          src="/images/archidekt_small.webp"
          height="200px">
          <div className={styles.headerContent}>
            <div className={styles.userInfo}>
              <div className={styles.collectionInfo}>
                <h1>
                  {owner.username.toLocaleLowerCase().endsWith('s') ? `${owner.username}'` : `${owner.username}'s`}{' '}
                  collection
                </h1>
                <ArchidektDropdown
                  menuClassName={styles.collectionTypeMenu}
                  options={[
                    {
                      label: (
                        <span className={styles.customIconOption}>
                          <Icon name="paste" /> Paper
                        </span>
                      ),
                      onClick: () => setCollectionQueryParams({ game: 1 }),
                    },
                    {
                      label: (
                        <span className={styles.customIconOption}>
                          <Icon className="mtgo" /> MTGO
                        </span>
                      ),
                      onClick: () => setCollectionQueryParams({ game: 2 }),
                    },
                    {
                      label: (
                        <span className={styles.customIconOption}>
                          <Icon className="arena" /> Arena
                        </span>
                      ),
                      onClick: () => setCollectionQueryParams({ game: 3 }),
                    },
                  ]}>
                  <div className={styles.collectionTypeDropdownContainer}>
                    Collection type:
                    <button className={styles.collectionTypeDropdown}>
                      {COLLECTION_GAME_TYPE[game || 1]} <Icon name="chevron circle down" />
                    </button>
                  </div>
                </ArchidektDropdown>
              </div>

              <div className={styles.avatar}>
                <AvatarCircle src={owner.avatar} username={owner.username} />
                <Link href={`/u/${encodeUsernameRoute(owner.username)}`}>{owner.username}</Link>
              </div>
            </div>

            <HeaderControls />
          </div>
        </CardBanner>

        <div className={styles.container}>
          <div className={styles.content}>
            <ContentControls />

            {view === 'text' && autoColumns && !viewingLabels && <TextView />}
            {view === 'image' && !viewingLabels && <ImageView />}

            {viewingLabels && <LabelsView />}
          </div>

          {view === 'text' && !autoColumns && !viewingLabels && (
            <div className={styles.scrollingTableContainer}>
              <TextView />
            </div>
          )}

          {!viewingLabels && (
            <PaginationControls page={page} totalPages={totalPages} className={styles.paginationContainer} />
          )}
        </div>

        <CardDetailsModal />
        <CollectionFilterModal />
        <KeybindsOverlay />

        {!Object.keys(multiSelectedIds).length && (
          <button className={styles.scrollToTop} onClick={() => window?.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Icon name="arrow up" />
            Scroll to top
          </button>
        )}
      </PageWrapper>
    </ImportDropWrapper>
  )
}

export default CollectionPageV2

export const getServerSideProps: GetServerSideProps<Props & ReduxServerProps> = async context => {
  try {
    const username = `${context.params?.username}` || null
    const game = context.query.game || 1

    const queryParams = { ...context.query }

    const cachedReduxState = generateInitialState(context.req.cookies)
    const pageSize = queryParams.pageSize || cachedReduxState.preferredPageSize || 100

    // @ts-ignore - numbers will be stringified in query string
    if (pageSize) queryParams.pageSize = pageSize

    // prettier-ignore
    const collectionData = await new CollectionService(context).listV2(`${username}`, queryString.stringify(queryParams))
    const tags = collectionData.tags.reduce((acc, tag) => {
      acc[tag.id] = tag

      return acc
    }, {} as Record<number, CollectionTag>)

    const serverCollectionData = new Array<number>()
    const collectionCards: Record<number, CollectionV2Card> = {}

    for (const collectionCard of collectionData.results) {
      serverCollectionData.push(collectionCard.id)

      const serializedCollectionCard = responseToCard(collectionCard.card)

      // @ts-ignore - Types are wrong from existing V1 endpoint code
      collectionCards[collectionCard.id] = { ...collectionCard, card: serializedCollectionCard }
    }

    return {
      props: {
        owner: collectionData.owner,
        page: collectionData.page,
        totalPages: collectionData.totalPages,
        count: collectionData.count,
        game: game,

        redux: {
          collectionV2: {
            ...cachedReduxState,
            preferredPageSize: pageSize,
            hasFetchedCollectionTags: true, // We've gotten them from loading the collection directly
            ownsCollection: context.req.cookies?.tbId === username,
            numberOfResults: collectionData.count,
            serverCollectionData,
            collectionCards,
            tags,
            game,
            isPublic: collectionData.isPublic,
          },
        },
      },
    }
  } catch (err) {
    return LOG_ERROR_AND_REDIRECT(err, context)
  }
}
